import { defineAsyncComponent } from '../../../lib/utils/vue3compat/vue.js?vue3';
import { GlButton, GlTooltipDirective, GlDisclosureDropdown, GlDisclosureDropdownItem, GlDisclosureDropdownGroup } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { __, sprintf } from '~/locale';
import UserAccessRoleBadge from '../../../vue_shared/components/user_access_role_badge.vue?vue3';
import ReplyButton from '../../../notes/components/note_actions/reply_button.vue?vue3';
import { getMutation, optimisticAwardUpdate, getNewCustomEmojiPath } from '../../notes/award_utils.js?vue3';
import { lowercaseWorkItemType } from '../../utils.js?vue3';
export default {
    name: 'WorkItemNoteActions',
    i18n: {
        editButtonText: __('Edit comment'),
        moreActionsText: __('More actions'),
        deleteNoteText: __('Delete comment'),
        copyLinkText: __('Copy link'),
        assignUserText: __('Assign to comment author'),
        unassignUserText: __('Unassign comment author'),
        reportAbuseText: __('Report abuse'),
        resolveThreadTitle: __('Resolve thread')
    },
    components: {
        EmojiPicker: defineAsyncComponent(()=>import('../../../emoji/components/picker.vue?vue3')),
        GlButton,
        GlDisclosureDropdown,
        GlDisclosureDropdownItem,
        GlDisclosureDropdownGroup,
        ReplyButton,
        UserAccessRoleBadge,
        ViewSessionButton: defineAsyncComponent(()=>import('../../../../../../ee/app/assets/javascripts/ai/shared/widgets/view_session_button.vue?vue3'))
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    props: {
        fullPath: {
            type: String,
            required: true
        },
        workItemIid: {
            type: String,
            required: true
        },
        note: {
            type: Object,
            required: true
        },
        showReply: {
            type: Boolean,
            required: true
        },
        showEdit: {
            type: Boolean,
            required: true
        },
        showAwardEmoji: {
            type: Boolean,
            required: false,
            default: false
        },
        noteUrl: {
            type: String,
            required: false,
            default: ''
        },
        isAuthorAnAssignee: {
            type: Boolean,
            required: false,
            default: false
        },
        showAssignUnassign: {
            type: Boolean,
            required: false,
            default: false
        },
        canReportAbuse: {
            type: Boolean,
            required: false,
            default: false
        },
        workItemType: {
            type: String,
            required: true
        },
        isWorkItemAuthor: {
            type: Boolean,
            required: false,
            default: false
        },
        isAuthorContributor: {
            type: Boolean,
            required: false,
            default: false
        },
        maxAccessLevelOfAuthor: {
            type: String,
            required: false,
            default: ''
        },
        projectName: {
            type: String,
            required: false,
            default: ''
        },
        canResolve: {
            type: Boolean,
            required: false,
            default: false
        },
        isResolved: {
            type: Boolean,
            required: false,
            default: false
        },
        isResolving: {
            type: Boolean,
            required: false,
            default: false
        },
        resolvedBy: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        duoSessionId: {
            type: Number,
            required: false,
            default: null
        }
    },
    emits: [
        'error',
        'resolve',
        'start-replying',
        'start-editing',
        'notify-copy-done',
        'assign-user',
        'report-abuse',
        'delete-note'
    ],
    computed: {
        assignUserActionText () {
            return this.isAuthorAnAssignee ? this.$options.i18n.unassignUserText : this.$options.i18n.assignUserText;
        },
        displayAuthorBadgeText () {
            return sprintf(__('This user is the author of this %{workItemType}.'), {
                workItemType: lowercaseWorkItemType(this.workItemType)
            });
        },
        displayMemberBadgeText () {
            return sprintf(__('This user has the %{access} role in the %{name} project.'), {
                access: this.maxAccessLevelOfAuthor.toLowerCase(),
                name: this.projectName
            });
        },
        displayContributorBadgeText () {
            return sprintf(__('This user has previously committed to the %{name} project.'), {
                name: this.projectName
            });
        },
        resolveIcon () {
            if (!this.isResolving) {
                return this.isResolved ? 'check-circle-filled' : 'check-circle';
            }
            return null;
        },
        resolveThreadTitle () {
            return this.isResolved ? __('Resolved by ') + this.resolvedBy.name : this.$options.i18n.resolveThreadTitle;
        },
        customEmojiPath () {
            return getNewCustomEmojiPath({
                cache: this.$apollo.provider.clients.defaultClient,
                fullPath: this.fullPath,
                workItemIid: this.workItemIid
            });
        }
    },
    methods: {
        async setAwardEmoji (name) {
            const { mutation, mutationName, errorMessage } = getMutation({
                note: this.note,
                name
            });
            try {
                await this.$apollo.mutate({
                    mutation,
                    variables: {
                        awardableId: this.note.id,
                        name
                    },
                    optimisticResponse: {
                        [mutationName]: {
                            errors: []
                        }
                    },
                    update: optimisticAwardUpdate({
                        note: this.note,
                        name,
                        fullPath: this.fullPath,
                        workItemIid: this.workItemIid
                    })
                });
            } catch (error) {
                this.$emit('error', errorMessage);
                Sentry.captureException(error);
            }
        },
        emitEvent (eventName) {
            this.$emit(eventName);
            this.$refs.dropdown.close();
        }
    }
};
